import { ListaColunaConfig } from '../components/models/lista-coluna-config.model';
import { AutorizacaoManualModel } from '../model/autorizacao-manual.model';

/**
 * Configuração de colunas da grade de autorização manual.
 * Os campos referenciam atributos de `AutorizacaoManualModel`, que faz
 * o adapter a partir do DTO `SwapSessao`.
 *
 * As larguras (`larguraMinPx`) foram dimensionadas para que cada coluna
 * caiba em uma única linha com os formatos esperados (CPF, celular,
 * cartão mascarado, datas dd/MM/yyyy HH:mm etc.). O container do ListBox
 * já tem `overflow-x: auto`, garantindo scroll horizontal.
 *
 * Ordenação server-side: somente as colunas com `valorOrdenacaoBackend`
 * preenchido aparecem no combo "Ordenar por". A proc
 * `PR_SWAP_SESSAO_CONSULTA` aceita 12 valores em `@ORDENACAO`; mapeamos
 * apenas os 7 que possuem coluna correspondente exibida na grade.
 */
export const AUTORIZACAO_MANUAL_COLUNAS: ListaColunaConfig[] = [
  {
    id: 'codSess',
    titulo: 'ID',
    campo: 'codSessao',
    ordenavel: false,
    larguraMinPx: 80,
    larguraExcel: 12,
  },
  {
    id: 'dataCri',
    titulo: 'Data',
    campo: 'dataCriacao',
    modoCelula: 'dataHora',
    ordenacaoPadrao: true,
    ordenacaoDirecao: 'desc',
    larguraMinPx: 120,
    valorOrdenacaoBackend: 'DTH_CRIACAO',
  },
  {
    id: 'dataVal',
    titulo: 'Validade',
    campo: 'dataValidade',
    modoCelula: 'dataHora',
    ordenavel: false,
    larguraMinPx: 120,
  },
  {
    id: 'etapa',
    titulo: 'Etapa',
    campo: 'etapa',
    ordenavel: false,
    larguraMinPx: 130,
  },
  {
    id: 'cpf',
    titulo: 'CPF',
    campo: 'cpfMotorista',
    modoCelula: 'cpf',
    ordenavel: false,
    larguraMinPx: 130,
  },
  {
    id: 'nome',
    titulo: 'Nome',
    campo: 'nomeMotorista',
    larguraMinPx: 190,
    valorOrdenacaoBackend: 'DSC_NOME_MOTORISTA',
  },
  {
    id: 'cel',
    titulo: 'Celular',
    campo: 'celularFormatado',
    ordenavel: false,
    larguraMinPx: 130,
  },
  {
    id: 'placa',
    titulo: 'Placa',
    campo: 'placa',
    larguraMinPx: 90,
    valorOrdenacaoBackend: 'DSC_PLACA',
  },
  {
    id: 'cartao',
    titulo: 'Cartão',
    campo: 'cartaoMascarado',
    larguraMinPx: 100,
    valorOrdenacaoBackend: 'DSC_CARTAO',
  },
  {
    id: 'comb',
    titulo: 'Combustível',
    campo: 'combustivel',
    larguraMinPx: 110,
    valorOrdenacaoBackend: 'DSC_COMBUSTIVEL',
  },
  {
    id: 'preco',
    titulo: 'Preço unit.',
    campo: 'precoUnitario',
    alinhamento: 'direita',
    modoCelula: 'moeda',
    ordenavel: false,
    larguraMinPx: 95,
  },
  {
    id: 'litros',
    titulo: 'Volume',
    campo: 'litros',
    alinhamento: 'direita',
    modoCelula: 'litros',
    ordenavel: false,
    larguraMinPx: 80,
  },
  {
    id: 'vtot',
    titulo: 'Valor total',
    campo: 'valorTotal',
    alinhamento: 'direita',
    modoCelula: 'moeda',
    larguraMinPx: 105,
    valorOrdenacaoBackend: 'VLR_TOTAL',
  },
  {
    id: 'hodo',
    titulo: 'Hodômetro',
    campo: 'hodometro',
    alinhamento: 'direita',
    formatoNumero: '1.0-0',
    ordenavel: false,
    larguraMinPx: 90,
  },
  {
    id: 'aut',
    titulo: 'Autorizado',
    campo: 'autorizado',
    alinhamento: 'centro',
    modoCelula: 'simNaoCor',
    ordenavel: false,
    larguraMinPx: 90,
  },
  {
    id: 'err',
    titulo: 'Erro auditoria',
    /** `MSG_ERRO_AUDITORIA` ou `DSC_RESTRICAO_AUDITORIA` (proc). */
    campo: 'msgErroAuditoria',
    modoCelula: 'textoErro',
    ordenavel: false,
    larguraMinPx: 220,
  },
  {
    id: 'mcc',
    titulo: 'Mcc',
    /** `AUTORIZACAO.COD_MCC` (abastecimento/autorização vinculada). */
    campo: 'codMcc',
    /** Descrição do MCC no tooltip (`dscMcc`). */
    campoTooltip: 'dscMcc',
    ordenavel: false,
    larguraMinPx: 80,
  },
  {
    id: 'codPosto',
    titulo: 'Cód. posto',
    campo: 'codPosto',
    ordenavel: false,
    larguraMinPx: 110,
  },
  {
    id: 'nomePosto',
    titulo: 'Nome posto',
    campo: 'nomePosto',
    ordenavel: false,
    larguraMinPx: 220,
  },
  {
    id: 'authn',
    titulo: 'Autorização',
    campo: 'codAutorizacao',
    ordenavel: false,
    larguraMinPx: 145,
  },
  {
    id: 'cab',
    titulo: 'Cód. abastec.',
    campo: 'codAbastecimento',
    larguraMinPx: 140,
    valorOrdenacaoBackend: 'COD_ABASTECIMENTO',
  },
  {
    id: 'dthUlt',
    titulo: 'Atualização',
    campo: 'dataUltimaAtividade',
    modoCelula: 'dataHora',
    ordenavel: false,
    larguraMinPx: 120,
  },
  {
    id: 'imgs',
    titulo: 'Imagens',
    campo: '',
    alinhamento: 'centro',
    modoCelula: 'iconesImagens',
    ordenavel: false,
    larguraMinPx: 100,
    iconesImagensConfig: {
      icones: [
        {
          id: 'BOMBA',
          icone: 'local_gas_station',
          tooltip: 'Imagem da bomba',
          mostrarSe: (l) => (l as AutorizacaoManualModel).flgImgBomba === true,
        },
        {
          id: 'PANEL',
          icone: 'dashboard',
          tooltip: 'Imagem do painel (odômetro)',
          mostrarSe: (l) => (l as AutorizacaoManualModel).flgImgPainel === true,
        },
        {
          id: 'PLACA',
          icone: 'directions_car',
          tooltip: 'Imagem da placa',
          mostrarSe: (l) => (l as AutorizacaoManualModel).flgImgPlaca === true,
        },
      ],
    },
  },
  {
    id: 'acao',
    titulo: 'Ação',
    campo: '',
    alinhamento: 'centro',
    modoCelula: 'botaoAcao',
    ordenavel: false,
    larguraMinPx: 110,
    botaoAcaoConfig: {
      rotulo: 'Bypass',
      cor: 'warn',
      exibirQuando: (linha) => (linha as AutorizacaoManualModel).bypassDisponivel,
    },
  },
];
